import React, { forwardRef, useEffect, useMemo, useState } from "react"
import { Line } from "react-chartjs-2"
import "chart.js/auto"
import zoomPlugin from "chartjs-plugin-zoom"
import { color_tones, lighter_rgba, to_rgba } from "@features/results/Result/const"
import { RunDto, RunResultsHistoric, RunResultsHistoricAggregate } from "@custom-types/modelRun"
import { Chart, ChartData, ChartOptions, DefaultDataPoint } from "chart.js"
import { getRunNamesMap } from "@utils/run"
import { getNumberWithOrdinal } from "@features/results/const"
import { GroupNodeLabel } from "@features/graph/graphViewModel"
import {
  getNodeUnitByLabel,
  graphViewGroupNodeLabelToSampleNode,
} from "@features/graph/utils/graphUtils"
import { Box, Typography } from "@mui/material"
import {
  getHistoricValues,
  getNodeTableRowsFromAggregate,
  getQuantileValue,
  getTicks,
  plugins,
} from "@features/results/Result/ChartsContainer/TimeSeriesChart.utils"
import NodeResultsTable from "@features/results/Result/ChartsContainer/NodeResultsTable"
import { getNodeResults } from "@features/results/Result/runResult.utils"
import { generateChartLabel } from "@utils/chartLabelUtils"

Chart.register(zoomPlugin)

type TimeSeriesChartProps = {
  title: string | null
  historicNodes: RunResultsHistoric | undefined
  percentiles: number[]
  showMeanAggregates: boolean
  showMeanLine: boolean
  showStDev?: boolean
  lagShift: number
  runs: RunDto[]
  nodes: GroupNodeLabel[] | null
  isMultipleNodesMode: boolean
  maxHeight?: number | string
  maintainAspectRatio?: boolean
  legendPosition?: "top" | "bottom"
  showAsTable?: boolean
  showLegend?: boolean
  showHistoric?: boolean
  aggregateData: RunResultsHistoricAggregate | undefined
  hiddenDatasets?: string[]
  onLegendClick?: (datasetLabel: string) => void
}

export type MeanDataSetsType = {
  data: {
    dev1_low: Record<string, number>
    dev2_low: Record<string, number>
    dev3_low: Record<string, number>
    dev1_up: Record<string, number>
    dev2_up: Record<string, number>
    dev3_up: Record<string, number>
    mean: Record<string, number>
  }
  unit_format: string
  run_key: string
  node_key: string
}

export interface MedianDataSetsType {
  data: {
    median: Record<string, number>
    mean: Record<string, number>
    percentiles: Record<number, Record<string, number>>
  }
  unit_format: string
  run_key: string
  node_key: string
}

export type HistoricData = {
  [key: string]: number
}

// TODO KK: correctly extend custom plugin configuration
// https://www.chartjs.org/docs/latest/developers/plugins.html#typescript-typings
export type HistoricLineOptions = ChartOptions<"line"> & {
  plugins: { historic: { show: boolean; right: number; left: number } }
}

export const TimeSeriesChart = forwardRef(function TimeSeriesChart(
  {
    title,
    historicNodes: allRunsNodeResultsWithHistoricData,
    percentiles,
    showMeanAggregates,
    showMeanLine,
    showStDev,
    lagShift,
    runs,
    nodes,
    maxHeight = 500,
    maintainAspectRatio = false,
    legendPosition = "top",
    showLegend = true,
    showAsTable = false,
    showHistoric = false,
    aggregateData,
    hiddenDatasets = [],
    onLegendClick,
  }: TimeSeriesChartProps,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
  chartRef: any,
) {
  //array of nodes that contains min,avg,max datasets
  const [medianDataSets, setMedianDataSets] = useState<MedianDataSetsType[]>([])
  const [meanDataSets, setMeanDataSets] = useState<MeanDataSetsType[]>([])
  const [timestepLabels, setTimestepLabels] = useState<number[]>([])
  const runNamesMap = getRunNamesMap(runs)

  useEffect(() => {
    if (!aggregateData || !allRunsNodeResultsWithHistoricData) return
    const medianResults: MedianDataSetsType[] = []
    const meanResults: MeanDataSetsType[] = []
    const timestepsArray: number[] = []

    Object.entries(aggregateData.results).forEach(([runKey, aggregateResults]) => {
      Object.entries(aggregateResults).forEach(([nodeKey, aggregateResult]) => {
        const dataSetMedian: Record<string, number> = {}
        const dataSetPercentiles: Record<number, Record<string, number>> = {}
        const unitFormat = aggregateResult.unitFormat || getNodeUnitByLabel(nodeKey)

        const meanResult: MeanDataSetsType = {
          unit_format: unitFormat,
          run_key: runKey,
          node_key: nodeKey,
          data: {
            dev1_low: {},
            dev2_low: {},
            dev3_low: {},
            dev1_up: {},
            dev2_up: {},
            dev3_up: {},
            mean: {},
          },
        }
        // guarantee data is orderly
        const sortedTimesteps = Object.keys(aggregateResult.timesteps).sort((a, b) =>
          parseInt(a) > parseInt(b) ? 1 : -1,
        )
        sortedTimesteps.forEach((timestepString, index) => {
          const aggregatedResultsItem = aggregateResult.timesteps[timestepString]
          const rawResultsItem = getNodeResults(allRunsNodeResultsWithHistoricData, runKey, nodeKey)
            ?.timesteps?.[timestepString]
          if (!aggregatedResultsItem || !rawResultsItem) return
          const timestep = parseInt(timestepString)

          if (index >= lagShift) {
            timestepsArray.push(timestep)
            dataSetMedian[timestep] = aggregatedResultsItem.median
            percentiles.forEach((percentile) => {
              const percentileValues = dataSetPercentiles[percentile] ?? {}
              percentileValues[timestep] = getQuantileValue(
                percentile,
                aggregatedResultsItem,
                rawResultsItem,
              )
              dataSetPercentiles[percentile] = percentileValues
            })

            const mean = aggregatedResultsItem.mean
            const stdev = aggregatedResultsItem.d3_deviation
            meanResult.data.mean[timestep] = mean
            meanResult.data.dev1_low[timestep] = mean - stdev
            meanResult.data.dev2_low[timestep] = mean - 2 * stdev
            meanResult.data.dev3_low[timestep] = mean - 3 * stdev
            meanResult.data.dev1_up[timestep] = mean + stdev
            meanResult.data.dev2_up[timestep] = mean + 2 * stdev
            meanResult.data.dev3_up[timestep] = mean + 3 * stdev
          }
        })

        medianResults.push({
          unit_format: unitFormat,
          node_key: nodeKey,
          run_key: runKey,
          data: {
            median: dataSetMedian,
            mean: meanResult.data.mean,
            percentiles: dataSetPercentiles,
          },
        })

        meanResults.push(meanResult)
      })
    })
    setMedianDataSets(medianResults)
    setMeanDataSets(meanResults)
    setTimestepLabels(Array.from(new Set(timestepsArray)).sort())
  }, [aggregateData, allRunsNodeResultsWithHistoricData, lagShift, percentiles])

  const uniqueUnitFormats = useMemo(() => {
    const allFormats = medianDataSets.map((dataset) => dataset.unit_format)
    return Array.from(new Set(allFormats))
  }, [medianDataSets])

  if (!aggregateData || !allRunsNodeResultsWithHistoricData) {
    return <Typography>No data to present</Typography>
  }

  const { data: historicValues, index: historicIndex } = getHistoricValues(
    allRunsNodeResultsWithHistoricData,
  )
  const historicEmptyValues: Record<string, number> = {}
  Object.entries(historicValues[historicIndex] ?? {}).forEach(([key]) => {
    historicEmptyValues[key] = NaN
  })
  const options: HistoricLineOptions = {
    responsive: true,
    maintainAspectRatio,
    interaction: {
      intersect: false,
      mode: "index",
    },
    scales: {
      x: {
        min: timestepLabels[0] || undefined,
        max: timestepLabels[timestepLabels.length - 1] || undefined,
      },
    },
    plugins: {
      historic: {
        show: showHistoric,
        right: timestepLabels.length,
        left: 0,
      },
      zoom: {
        limits: {
          x: { minRange: 2 },
        },
        zoom: {
          drag: {
            enabled: true,
          },
          pinch: {
            enabled: true,
          },
          mode: "x",
        },
      },
      legend: {
        position: legendPosition,
        display: showLegend,
        labels: {
          filter: function (item) {
            return Boolean(item.text)
          },
          boxWidth: 11,
          boxHeight: 11,
          font: {
            size: 11,
          },
        },
        onClick: function (event, legendItem, legend) {
          const index = legendItem.datasetIndex
          if (index === undefined) return

          const dataset = legend.chart.data.datasets[index]
          const datasetLabel = dataset?.label

          if (!datasetLabel) return

          // If onLegendClick callback is provided, use it instead of default behavior
          if (onLegendClick) {
            onLegendClick(datasetLabel)
            return
          }

          // Default behavior (when onLegendClick is not provided)
          const yAxisID = dataset?.yAxisID

          if (yAxisID) {
            // we don't want to hide an axis if another series is still visible
            let othersVisible = false
            for (let i = 0; i < legend.chart.data.datasets.length; i++) {
              if (legend.chart.data.datasets[i]?.yAxisID === yAxisID) {
                if (!legend.chart.data.datasets[i]?.hidden && legendItem.datasetIndex !== i) {
                  othersVisible = true
                }

                if (legendItem.datasetIndex === i) {
                  const ds = legend.chart.data.datasets[i]
                  if (ds) ds.hidden = !ds.hidden
                }
              }
            }

            const scale = legend.chart.scales[yAxisID]
            if (!othersVisible && scale) scale.options.display = !scale.options.display

            legend.chart.update()
          }
        },
      },
      title: {
        display: false,
      },
      filler: {
        propagate: false,
      },
      tooltip: {
        intersect: false,
        mode: "index",
      },
    },
    animation: {
      duration: 0,
    },
  }
  if (showHistoric) {
    const labels = Object.keys(historicValues[historicIndex] ?? {})
      .map((x) => parseInt(x))
      .sort()
    if (options.scales?.x?.min) options.scales.x.min = labels[0]
  }

  uniqueUnitFormats.forEach((format, index) => {
    options.scales![format || "y"] = {
      ticks: getTicks(format, index, uniqueUnitFormats.length),
      type: "linear",
      display: true,
      position: index <= 1 ? "left" : "right",
      grid: {
        drawOnChartArea: false,
      },
    }
  })

  const getTimestepLabels = () => {
    if (showHistoric) {
      const labels = Object.keys(historicValues[historicIndex] ?? {})
        .map((x) => parseInt(x))
        .sort()
        .filter((l) => timestepLabels.indexOf(l) === -1)
      return [...labels, ...timestepLabels]
    }
    return timestepLabels
  }

  const chartData: ChartData<"line", DefaultDataPoint<"line">, number> = {
    labels: getTimestepLabels(),
    datasets: [],
  }

  const filledMedianDataSets: MedianDataSetsType[] = medianDataSets.map((dataset, datasetIndex) => {
    const filledMissingTimesteps = structuredClone(dataset)
    filledMissingTimesteps.data.median = timestepLabels.reduce<Record<string, number>>(
      (acc, timestepLabel) => {
        acc[timestepLabel] = dataset.data.median[timestepLabel] ?? NaN
        return acc
      },
      {},
    )
    filledMissingTimesteps.data.mean = timestepLabels.reduce<Record<string, number>>(
      (acc, timestepLabel) => {
        acc[timestepLabel] = dataset.data.mean[timestepLabel] ?? NaN
        return acc
      },
      {},
    )
    Object.keys(filledMissingTimesteps.data.percentiles).forEach((percentileKey) => {
      const percentile = Number(percentileKey)
      filledMissingTimesteps.data.percentiles[percentile] = timestepLabels.reduce<
        Record<string, number>
      >((acc, timestepLabel) => {
        acc[timestepLabel] = dataset.data.percentiles[percentile]?.[timestepLabel] ?? NaN
        return acc
      }, {})
    })

    if (showHistoric) {
      const historic = historicValues[datasetIndex]
      // if some data have missing historic values we need to push empty values so
      const values = Object.keys(historic ?? {}).length > 0 ? historic : historicEmptyValues
      filledMissingTimesteps.data.median = { ...filledMissingTimesteps.data.median, ...values }
      filledMissingTimesteps.data.mean = {
        ...filledMissingTimesteps.data.mean,
        ...historicEmptyValues,
      }
      Object.keys(filledMissingTimesteps.data.percentiles).forEach((percentileKey) => {
        const percentile = Number(percentileKey)
        const percentileValues = filledMissingTimesteps.data.percentiles[percentile]
        if (!percentileValues) return

        filledMissingTimesteps.data.percentiles[percentile] = {
          ...percentileValues,
          ...historicEmptyValues,
        }
      })
    }
    return filledMissingTimesteps
  })

  const filledMeanDataSets: MeanDataSetsType[] = meanDataSets.map((dataset, datasetIndex) => {
    const filledMissingTimesteps = structuredClone(dataset)

    Object.entries(dataset.data).forEach(([aggregate, results]) => {
      const aggregateKey = aggregate as keyof MeanDataSetsType["data"]

      filledMissingTimesteps.data[aggregateKey] = timestepLabels.reduce<Record<string, number>>(
        (acc, timestepLabel) => {
          if (results[timestepLabel] !== undefined) {
            acc[timestepLabel] = results[timestepLabel]
          } else {
            acc[timestepLabel] = NaN
          }
          return acc
        },
        {},
      )
    })

    if (showHistoric) {
      const historic = historicValues[datasetIndex]
      for (const key of Object.keys(filledMissingTimesteps.data)) {
        const aggregateKey = key as keyof MeanDataSetsType["data"]
        // historic values > sampling values
        filledMissingTimesteps.data[aggregateKey] = {
          ...filledMissingTimesteps.data[aggregateKey],
          ...(aggregateKey === "mean" ? historic : historicEmptyValues),
        }
      }
    }

    return filledMissingTimesteps
  })

  const shouldShowMeanLine = showMeanLine || showMeanAggregates

  if (showMeanAggregates) {
    filledMeanDataSets.forEach((data, index) => {
      const colorTones = color_tones[index] ?? []
      const nodeKey = data.node_key

      // Get node label - prefer formatted label from nodes prop, fallback to nodeKey
      const groupNodeLabel = (nodes ?? []).find(
        (n) => graphViewGroupNodeLabelToSampleNode(n.label) === nodeKey,
      )
      const nodeName = groupNodeLabel?.label ?? nodeKey

      const currentRun = runs.find((run) => run.key === data.run_key)
      const runName = currentRun ? runNamesMap[currentRun.key] : undefined
      const runVersion = currentRun?.version

      const yAxisID = data.unit_format || "y"

      if (showStDev) {
        // TODO TR: extend this to allow user to pick/define
        chartData.datasets.push({
          label: generateChartLabel({
            nodeName,
            nodeUnit: data.unit_format,
            numberOfRuns: runs.length,
            runName,
            runVersion,
            statisticalSuffix: "μ+3σ",
          }),
          data: Object.values(data.data.dev3_up),
          borderColor: to_rgba(lighter_rgba(colorTones, 0, 0.4)),
          borderDash: [12, 5],
          borderWidth: 3,
          backgroundColor: to_rgba(lighter_rgba(colorTones, 0, 0.2)),
          pointRadius: 0,
          fill: "+3",
          yAxisID,
        })

        chartData.datasets.push({
          label: generateChartLabel({
            nodeName,
            nodeUnit: data.unit_format,
            numberOfRuns: runs.length,
            runName,
            runVersion,
            statisticalSuffix: "μ+2σ",
          }),
          data: Object.values(data.data.dev2_up),
          borderColor: to_rgba(lighter_rgba(colorTones, 0, 0.5)),
          borderDash: [6, 4],
          borderWidth: 3,
          backgroundColor: to_rgba(lighter_rgba(colorTones, 0, 0.3)),
          pointRadius: 0,
          fill: "+2",
          yAxisID,
        })

        chartData.datasets.push({
          label: generateChartLabel({
            nodeName,
            nodeUnit: data.unit_format,
            numberOfRuns: runs.length,
            runName,
            runVersion,
            statisticalSuffix: "μ+σ",
          }),
          data: Object.values(data.data.dev1_up),
          borderColor: to_rgba(lighter_rgba(colorTones, 0, 0.7)),
          borderDash: [3, 3],
          borderWidth: 3,
          backgroundColor: to_rgba(lighter_rgba(colorTones, 0, 0.5)),
          pointRadius: 0,
          fill: "+1",
          yAxisID,
        })
      }

      if (shouldShowMeanLine) {
        chartData.datasets.push({
          label: generateChartLabel({
            nodeName,
            nodeUnit: data.unit_format,
            numberOfRuns: runs.length,
            runName,
            runVersion,
          }),
          data: Object.values(data.data.mean),
          borderColor: to_rgba(lighter_rgba(colorTones, 0, 1)),
          backgroundColor: to_rgba(lighter_rgba(colorTones, 0, 0.5)),
          fill: "none",
          yAxisID,
        })
      }

      if (showStDev) {
        // TODO TR: extend this to allow user to pick/define
        chartData.datasets.push({
          label: generateChartLabel({
            nodeName,
            nodeUnit: data.unit_format,
            numberOfRuns: runs.length,
            runName,
            runVersion,
            statisticalSuffix: "μ-σ",
          }),
          data: Object.values(data.data.dev1_low),
          borderColor: to_rgba(lighter_rgba(colorTones, 0, 0.7)),
          borderDash: [3, 3],
          borderWidth: 3,
          backgroundColor: to_rgba(lighter_rgba(colorTones, 0, 0.5)),
          pointRadius: 0,
          fill: "-1",
          yAxisID,
        })

        chartData.datasets.push({
          label: generateChartLabel({
            nodeName,
            nodeUnit: data.unit_format,
            numberOfRuns: runs.length,
            runName,
            runVersion,
            statisticalSuffix: "μ-2σ",
          }),
          data: Object.values(data.data.dev2_low),
          borderColor: to_rgba(lighter_rgba(colorTones, 0, 0.5)),
          borderDash: [6, 4],
          borderWidth: 3,
          backgroundColor: to_rgba(lighter_rgba(colorTones, 0, 0.2)),
          pointRadius: 0,
          fill: "-2",
          yAxisID,
        })

        chartData.datasets.push({
          label: generateChartLabel({
            nodeName,
            nodeUnit: data.unit_format,
            numberOfRuns: runs.length,
            runName,
            runVersion,
            statisticalSuffix: "μ-3σ",
          }),
          data: Object.values(data.data.dev3_low),
          borderColor: to_rgba(lighter_rgba(colorTones, 0, 0.4)),
          borderDash: [12, 5],
          borderWidth: 3,
          backgroundColor: to_rgba(lighter_rgba(colorTones, 0, 0.2)),
          pointRadius: 0,
          fill: "-3",
          yAxisID,
        })
      }
    })
  } else {
    filledMedianDataSets.forEach((data, index) => {
      const nodeKey = data.node_key
      const colorTones = color_tones[index] ?? []

      const yAxisID = data.unit_format || "y"

      // Determine node name and run information
      const groupNodeLabel = (nodes ?? []).find(
        (n) => graphViewGroupNodeLabelToSampleNode(n.label) === nodeKey,
      )
      const nodeName = groupNodeLabel?.label ?? nodeKey
      const currentRun = runs.find((run) => run.key === data.run_key)
      const runName = currentRun ? runNamesMap[currentRun.key] : undefined
      const runVersion = currentRun?.version

      percentiles.forEach((percentile, percentileIndex) => {
        chartData.datasets.push({
          label: generateChartLabel({
            nodeName,
            nodeUnit: data.unit_format,
            numberOfRuns: runs.length,
            runName,
            runVersion,
            statisticalSuffix: `${getNumberWithOrdinal(percentile)} percentile`,
          }),
          data: Object.values(data.data.percentiles[percentile] ?? {}),
          borderColor: to_rgba(lighter_rgba(colorTones, 0, 0.7)),
          borderDash: percentileIndex === 0 ? [10, 5] : [3, 5],
          borderWidth: 3,
          backgroundColor: to_rgba(lighter_rgba(colorTones, 0, 0.2)),
          pointRadius: 0,
          fill: percentileIndex === 0 ? "+1" : "-1",
          order: 1,
          yAxisID,
        })
      })

      // order is important for fill prop
      // BUT - putting the major data here puts the item behind in z-order
      // so this is a transparent entry, with the same data- we will then add another line
      // on top of the shaded areas at the end
      chartData.datasets.push({
        label: generateChartLabel({
          nodeName,
          nodeUnit: data.unit_format,
          numberOfRuns: runs.length,
          runName,
          runVersion,
        }),
        data: Object.values(data.data.median),
        borderColor: to_rgba(lighter_rgba(colorTones, 0, 1.0)),
        backgroundColor: to_rgba(lighter_rgba(colorTones, 0, 0.5)),
        borderWidth: 2,
        pointBorderColor: "rgba(0, 0, 0, 0)",
        pointBackgroundColor: "rgba(0, 0, 0, 0)",
        pointHitRadius: 20,
        pointHoverBackgroundColor: to_rgba(lighter_rgba(colorTones, 0, 0.5)),
        pointHoverBorderColor: to_rgba(lighter_rgba(colorTones, 0, 1.0)),
        fill: "none",
        order: 0,
        yAxisID,
      })

      // Mean line should always be added as last so it is not affected by the fill of the percentile areas
      if (shouldShowMeanLine) {
        chartData.datasets.push({
          label: generateChartLabel({
            nodeName,
            nodeUnit: data.unit_format,
            numberOfRuns: runs.length,
            runName,
            runVersion,
            statisticalSuffix: "mean",
          }),
          data: Object.values(data.data.mean),
          borderColor: to_rgba(lighter_rgba(colorTones, 0, 1.0)),
          backgroundColor: to_rgba(lighter_rgba(colorTones, 0, 0.5)),
          borderWidth: 2,
          pointBorderColor: "rgba(0, 0, 0, 0)",
          pointBackgroundColor: "rgba(0, 0, 0, 0)",
          borderDash: [40, 2],
          pointHoverBackgroundColor: to_rgba(lighter_rgba(colorTones, 0, 0.5)),
          pointHoverBorderColor: to_rgba(lighter_rgba(colorTones, 0, 1.0)),
          fill: "none",
          order: 0,
          yAxisID,
        })
      }
    })
  }

  // Apply hidden state to datasets based on hiddenDatasets prop
  chartData.datasets.forEach((dataset) => {
    if (dataset.label) {
      dataset.hidden = hiddenDatasets?.includes(dataset.label) ?? false
    }
  })

  if (showAsTable) {
    const tableRows = getNodeTableRowsFromAggregate(
      aggregateData,
      allRunsNodeResultsWithHistoricData,
      runs,
      nodes ?? [],
      lagShift,
      percentiles,
      showMeanAggregates,
      shouldShowMeanLine,
      showHistoric,
      historicValues,
    )
    return <NodeResultsTable rows={tableRows} exportFileName={title + "-" + runs[0]?.key} />
  }

  return (
    <Box sx={{ height: maxHeight, overflowY: "hidden" }}>
      <Line ref={chartRef} options={options} data={chartData} plugins={plugins} />
    </Box>
  )
})
